import { formatSar } from '@wasity/shared';
import type { Locale } from '@/i18n';

export const money = (halalas: number | null | undefined, locale: Locale) => (halalas == null ? '—' : formatSar(halalas, locale));

/** Date only, Western digits, Riyadh time. */
export function dateOnly(d: string | Date | null | undefined, locale: Locale) {
  if (!d) return '—';
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-nu-latn-ca-gregory' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Riyadh' }).format(new Date(d));
}
/** Weekday + date + time, for viewing slots. */
export function slotLabel(d: string | Date | null | undefined, locale: Locale) {
  if (!d) return '—';
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-nu-latn-ca-gregory' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Riyadh' }).format(new Date(d));
}
export function timeOnly(d: string | Date, locale: Locale) {
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-nu-latn-ca-gregory' : 'en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Riyadh' }).format(new Date(d));
}
/** +966501234567 → +966 50 123 4567 */
export function phoneDisplay(e164: string | null | undefined) {
  if (!e164) return '—';
  const m = /^\+966(\d{2})(\d{3})(\d{4})$/.exec(e164);
  return m ? `+966 ${m[1]} ${m[2]} ${m[3]}` : e164;
}
/** ٠-٩ are digits too, and this is an Arabic-first panel. */
const westernDigits = (v: string) => v.replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660));

/**
 * SAR input ("1,850,000" or "1850000" or "١٨٥٠٠٠٠") → halalas, or null when it is not a price.
 *
 * It used to strip every character that was not a digit or a dot, which quietly turned "-1850000"
 * into a real, binding offer of 1,850,000 SAR and "1e9" into 19. A minus sign or an exponent means
 * the person typed something this field cannot take, and the answer to that is to refuse it — so the
 * separators a person actually writes are removed, and everything else has to parse as a number.
 */
export function parseSar(input: string): number | null {
  const cleaned = westernDigits(String(input)).replace(/[,\u066C\u066B\s]/g, (m) => (m === '\u066B' ? '.' : ''));
  if (!/^\d+(\.\d+)?$/.test(cleaned.trim())) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
}

/**
 * Halalas → what goes in a SAR text box.
 *
 * Rounding to whole riyals here meant that opening a listing priced 1,500,000.55, changing only its
 * title and saving wrote 1,500,001.00 back — the form re-sends every field, so a lossy round trip
 * through this function was a silent price change.
 */
export const toSarInput = (halalas: number | null | undefined) => (halalas == null ? '' : String(halalas / 100));

/**
 * A calendar day in Riyadh, `YYYY-MM-DD`, as a date input wants it.
 *
 * `new Date(...).toISOString().slice(0, 10)` is the UTC day, which between midnight and 03:00 Riyadh
 * is yesterday — so a picker meaning "tomorrow" opened on today, and "today" was a day that had
 * already started. Everything else in the panel is formatted in Riyadh time; this is the same.
 */
export function riyadhDay(at: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Riyadh', year: 'numeric', month: '2-digit', day: '2-digit' }).format(at);
}
